/**
 * An item modifier is a type of menu item that serves as an addition to a menu item.
 * Read more about [item modifiers](https://dev.wix.com/docs/rest/business-solutions/restaurants/menus/item-modifiers/introduction).
 */
export interface Modifier {
  /**
   * Item modifier ID.
   * @format GUID
   * @readonly
   */
  id?: string | null;
  /**
   * Revision number, which increments by 1 each time the item modifier is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the item modifier. <br />
   *
   * Ignored when creating an item modifier.
   * @readonly
   */
  revision?: string | null;
  /**
   * Date and time the item modifier was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time the item modifier was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Item modifier display name.
   * @minLength 1
   * @maxLength 200
   */
  name?: string | null;
  /** Extended fields. */
  extendedFields?: ExtendedFields;
  /**
   * Whether the modifier is in stock.
   * Default: `true`.
   */
  inStock?: boolean | null;
  /**
   * IDs of the business locations ([SDK](https://dev.wix.com/docs/sdk/backend-modules/restaurants/wix-restaurants-new/about-business-locations) | [REST](https://dev.wix.com/docs/rest/business-solutions/restaurants/wix-restaurants-new/about-business-locations)) where these item modifiers are available.
   * @maxSize 100
   * @format GUID
   * @readonly
   */
  businessLocationIds?: string[];
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

export interface ExternalReferenceInfo {
  /**
   * ID of the external integrator app\system associated with this object.
   * @format GUID
   */
  externalAppId?: string | null;
  /**
   * Reference to an external resource ID. Used to link this object to a specific entity in an external system.
   * @maxLength 100
   */
  externalEntityId?: string | null;
  /** Last time this item modifier was synced with the external app. */
  lastSyncDate?: Date | null;
  /** Whether the connection to the external system is currently active. */
  connected?: boolean | null;
}

export interface InvalidateCache extends InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
  /**
   * tell us why you're invalidating the cache. You don't need to add your app name
   * @maxLength 256
   */
  reason?: string | null;
  /** Is local DS */
  localDc?: boolean;
  hardPurge?: boolean;
  /**
   * Optional caller-provided ID for tracking this invalidation through the system.
   * When set, the corresponding CDN purge completion event will include this ID,
   * allowing you to confirm when the invalidation has fully propagated.
   * Example: generate a UUID, pass it here, and later match it in the CDN purge completion event.
   * @maxLength 256
   */
  correlationId?: string | null;
}

/** @oneof */
export interface InvalidateCacheGetByOneOf {
  /**
   * Invalidate by msId. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Site ID. NOT recommended, as this will invalidate the entire site cache!
   * @format GUID
   */
  siteId?: string;
  /** Invalidate by App */
  app?: App;
  /** Invalidate by page id */
  page?: Page;
  /** Invalidate by URI path */
  uri?: URI;
  /** Invalidate by file (for media files such as PDFs) */
  file?: File;
  /** Invalidate by custom tag. Tags used in BO invalidation are disabled for this endpoint (more info: https://wix-bo.com/dev/clear-ssr-cache) */
  customTag?: CustomTag;
  /** Invalidate by multiple page ids */
  pages?: Pages;
  /** Invalidate by multiple URI paths */
  uris?: URIs;
}

export interface App {
  /**
   * The AppDefId
   * @minLength 1
   */
  appDefId?: string;
  /**
   * The instance Id
   * @format GUID
   */
  instanceId?: string;
}

export interface Page {
  /**
   * the msid the page is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by Page ID
   * @minLength 1
   */
  pageId?: string;
}

export interface URI {
  /**
   * the msid the URI is on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI path to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @minLength 1
   */
  uriPath?: string;
}

export interface File {
  /**
   * the msid the file is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by filename (for media files such as PDFs)
   * @minLength 1
   * @maxLength 256
   */
  fileName?: string;
}

export interface CustomTag {
  /**
   * the msid the tag is related to
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Tag to invalidate by
   * @minLength 1
   * @maxLength 256
   */
  tag?: string;
}

export interface Pages {
  /**
   * the msid the pages are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * Invalidate by multiple Page IDs in a single message
   * @maxSize 100
   * @minLength 1
   */
  pageIds?: string[];
}

export interface URIs {
  /**
   * the msid the URIs are on
   * @format GUID
   */
  metaSiteId?: string;
  /**
   * URI paths to invalidate (e.g. page/my/path) - without leading/trailing slashes
   * @maxSize 100
   * @minLength 1
   */
  uriPaths?: string[];
}

export interface CreateModifierRequest {
  /** Item modifier to create. */
  modifier: Modifier;
}

export interface CreateModifierResponse {
  /** Created item modifier. */
  modifier?: Modifier;
}

export interface GetModifierRequest {
  /**
   * ID of the item modifier to retrieve.
   * @format GUID
   */
  modifierId: string;
}

export interface GetModifierResponse {
  /** Retrieved item modifier. */
  modifier?: Modifier;
}

export interface ListModifiersRequest {
  /**
   * IDs of the item modifiers to retrieve.
   * @format GUID
   * @maxSize 500
   */
  modifierIds?: string[];
  /** Metadata of the paginated results. */
  paging?: CursorPaging;
}

export interface CursorPaging {
  /**
   * Maximum number of items to load.
   * @max 500
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   */
  cursor?: string | null;
}

export interface ListModifiersResponse {
  /** Retrieved item modifiers. */
  modifiers?: Modifier[];
  /** Metadata of the paginated results. */
  metadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Cursor pointing to the next page and the previous page in the list of results. */
  cursors?: Cursors;
  /**
   * Whether there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   */
  hasNext?: boolean | null;
}

export interface Cursors {
  /** Cursor pointing to next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to previous page in the list of results. */
  prev?: string | null;
}

export interface UpdateModifierRequest {
  /** Item Modifier with updated properties. */
  modifier: Modifier;
}

export interface UpdateModifierResponse {
  /** Updated item modifier. */
  modifier?: Modifier;
}

export interface BulkCreateModifiersRequest {
  /**
   * List of item modifiers to create.
   * @maxSize 100
   */
  modifiers: Modifier[];
  /**
   * Whether the created item modifiers are included in the response. <br />
   * Default: `false`.
   */
  returnEntity?: boolean;
}

export interface BulkCreateModifiersResponse {
  /** Information about the created item modifiers. */
  results?: BulkCreateModifierResult[];
  /** Metadata for Bulk Create Modifiers API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkCreateModifierResult {
  /** Metadata for creation of the item modifer. */
  itemMetadata?: ItemMetadata;
  /** Created item modifier. */
  modifier?: Modifier;
}

export interface ItemMetadata {
  /** Item ID. Should always be available, unless it's impossible (for example, when failing to create an item). */
  id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface BulkUpdateModifiersRequest {
  /**
   * List of item modifiers to update.
   * @minSize 1
   * @maxSize 100
   */
  modifiers: MaskedModifier[];
  /** Whether the updated item modifiers are included in the response. */
  returnEntity?: boolean;
}

export interface MaskedModifier {
  /** Item modifier to update. */
  modifier?: Modifier;
  /** Explicit list of fields to update. */
  mask?: string[];
}

export interface BulkUpdateModifiersResponse {
  /**
   * Information about the updated item modifiers.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateModifierResult[];
  /** Metadata for Bulk Update Modifiers API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateModifierResult {
  /** Metadata for the update of the item modifer. */
  itemMetadata?: ItemMetadata;
  /** Updated item modifier. */
  modifier?: Modifier;
}

export interface DeleteModifierRequest {
  /**
   * ID of the item modifier to delete.
   * @format GUID
   */
  modifierId: string;
}

export interface DeleteModifierResponse {}

export interface QueryModifiersRequest {
  /** Query options. */
  query?: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   * See [API Query Language](https://dev.wix.com/api/rest/getting-started/api-query-language)
   * for more information.
   */
  filter?: Record<string, any> | null;
  /** Sort object. */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface QueryModifiersResponse {
  /** Retrieved item modifiers. */
  modifiers?: Modifier[];
  /** Metadata of the paginated results. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CountModifiersRequest {
  /** Filter for counting modifiers. */
  filter?: Record<string, any> | null;
}

export interface CountModifiersResponse {
  /** Counted modifiers. */
  count?: number;
}

export interface BulkDeleteModifiersRequest {
  /**
   * Item Modifier IDs.
   * @format GUID
   * @maxSize 200
   */
  ids: string[];
}

export interface BulkDeleteModifiersResponse {
  /** Information about the deleted modifiers. */
  results?: BulkDeleteModifiersResult[];
  /** Metadata for the API call. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteModifiersResult {
  /** Metadata for modifiers deletion. */
  itemMetadata?: ItemMetadata;
}

export interface CloneModifiersRequest {
  /**
   * The MetaSiteId to clone from
   * @format GUID
   */
  metaSiteId?: string;
}

export interface CloneModifiersResponse {}

export interface BulkUpdateBusinessLocationIdsRequest {
  /**
   * modifier ids
   * @format GUID
   * @minSize 1
   * @maxSize 100
   */
  modifierIds?: string[];
  /**
   * Business Location Ids.
   * @maxSize 20
   * @format GUID
   */
  businessLocationIds?: string[];
}

export interface BulkUpdateBusinessLocationIdsResponse {
  /**
   * Information about the updated modifiers.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateBusinessLocationIdsResponseBulkItemResult[];
}

export interface BulkUpdateBusinessLocationIdsResponseBulkItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateBusinessLocationIdsPerEntityRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  modifierIdLocationIds?: ModifierIdLocationIds[];
}

export interface ModifierIdLocationIds {
  /**
   * modifier ID
   * @format GUID
   */
  modifierId?: string;
  /**
   * Business Location IDs
   * @maxSize 20
   * @format GUID
   */
  businessLocationIds?: string[];
}

export interface BulkUpdateBusinessLocationIdsPerEntityResponse {
  /**
   * Information about the updated modifiers.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkUpdateBusinessLocationIdsPerEntityResponseBulkItemResult[];
}

export interface BulkUpdateBusinessLocationIdsPerEntityResponseBulkItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
}

export interface BulkUpdateAttachedToOrphanItemRequest {
  /**
   * @minSize 1
   * @maxSize 100
   */
  modifierIdAttachedToOrphanItem?: ModifierIdAttachedToOrphanItem[];
}

export interface ModifierIdAttachedToOrphanItem {
  /**
   * Modifier ID
   * @format GUID
   */
  modifierId?: string;
  /** Attached to orphan item */
  attachedToOrphanItem?: boolean;
}

export interface BulkUpdateAttachedToOrphanItemResponse {
  /**
   * Information about the updated modifiers.
   * @minSize 1
   * @maxSize 100
   */
  results?: BulkItemResult[];
}

export interface BulkItemResult {
  /** Metadata for item update. */
  itemMetadata?: ItemMetadata;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface SyncContentToMultilingualRequest {
  /**
   * MetaSite ID
   * @format GUID
   */
  metaSiteId?: string;
}

export interface SyncContentToMultilingualResponse {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CountModifiersApplicationErrors = {
  code?: 'TOO_MANY_TO_COUNT';
  description?: string;
  data?: Record<string, any>;
};
